import React, {useCallback, useContext, useId} from 'react';
import {useIntl} from 'react-intl';

import CopyButton from 'components/copy_button';

import * as SyntaxHighlighting from 'utils/syntax_highlighting';

import PostContext from 'components/post_view/post_context';
import {copyToClipboard} from 'utils/copy_to_clipboard';
import {ContextMenu, ContextMenuTrigger} from 'components/widgets/context-menu';
import Menu from 'components/widgets/menu/menu';

type Props = {
    id: string;
    channelId: string;
    code: string;
    language: string;
    searchedContent?: string;
}

const CodeBlock: React.FC<Props> = ({id, code, language, searchedContent}: Props) => {
    const {formatMessage} = useIntl();
    const codeBlockId = useId();
    const {onLogPostAction} = useContext(PostContext);
    const getUsedLanguage = useCallback(() => {
        let usedLanguage = language || '';
        usedLanguage = usedLanguage.toLowerCase();

        if (usedLanguage === 'texcode' || usedLanguage === 'latexcode') {
            usedLanguage = 'latex';
        }

        // treat html as xml to prevent injection attacks
        if (usedLanguage === 'html') {
            usedLanguage = 'xml';
        }

        return usedLanguage;
    }, [language]);

    const usedLanguage = getUsedLanguage();

    let className = 'post-code';
    if (!usedLanguage) {
        className += ' post-code--wrap';
    }

    let header: JSX.Element = <></>;
    let lineNumbers: JSX.Element = <></>;
    if (SyntaxHighlighting.canHighlight(usedLanguage)) {
        header = (
            <span className='post-code__language'>
                {SyntaxHighlighting.getLanguageName(usedLanguage)}
            </span>
        );
        lineNumbers = (
            <div className='post-code__line-numbers'>
                {SyntaxHighlighting.renderLineNumbers(code)}
            </div>
        );
    }

    // If we have to apply syntax highlighting AND highlighting of search terms, create two copies
    // of the code block, one with syntax highlighting applied and another with invisible text, but
    // search term highlighting and overlap them
    const content = SyntaxHighlighting.highlight(usedLanguage, code);

    let htmlContent = content;
    if (searchedContent) {
        htmlContent = `${searchedContent}${content}`;
    }

    const copyText = () => {
        copyToClipboard(code);
        onLogPostAction('copy', 'rightClick');
    };

    const copyMarkdown = useCallback(() => {
        const markdown = `\`\`\`${language}\n${code}\n\`\`\``;
        copyToClipboard(markdown);
        onLogPostAction('copy', 'rightClick');
    }, [language, code, onLogPostAction]);

    const contextMenu = (
        <ContextMenu id={`copy-code-block-context-menu-${id}-${codeBlockId}`}>
            <Menu.ItemAction
                text={formatMessage({id: 'copy.message', defaultMessage: 'Copy'})}
                onClick={copyText}
            />
            <Menu.ItemAction
                text={formatMessage({id: 'copy.block.message', defaultMessage: 'Copy code block'})}
                onClick={copyMarkdown}
            />
        </ContextMenu>
    );

    return (
        <div className={className}>
            <div className='post-code__overlay'>
                <CopyButton onCopy={copyText}/>
                {header}
            </div>
            {contextMenu}
            <ContextMenuTrigger
                id={`copy-code-block-context-menu-${id}-${codeBlockId}`}
                holdToDisplay={-1}
            >
                <div className='hljs'>
                    {lineNumbers}
                    <code dangerouslySetInnerHTML={{__html: htmlContent}}/>
                </div>
            </ContextMenuTrigger>
        </div>
    );
};

export default CodeBlock;
